import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { planApi } from '../services/api';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';
import { usePlatoonStore } from '../store/platoonStore';
import { parsePlanBlocks, blockDef, type PlanBlock } from '../lib/opBlocks';
import { PHASE_META } from '../types/domain';
import BlockSequenceEditor from '../components/plans/BlockSequenceEditor';

type PlanStatus = 'DRAFT' | 'ISSUED' | 'ACTIVE' | 'COMPLETE';

interface Plan {
  id: string;
  name: string;
  unitId: string;
  unit?: { id: string; code: string; name: string } | null;
  platoonId: string | null;
  callsign: string | null;
  status: PlanStatus;
  blocksJson: string;
  hHour: string | null;
  progressJson: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Progress {
  advancedIndex: number;
  advances: Array<{ index: number; at: string; eventId?: string }>;
}

const LANES: { status: PlanStatus; label: string; tone: string }[] = [
  { status: 'DRAFT', label: 'Draft', tone: 'text-gray-400 border-command-border' },
  { status: 'ISSUED', label: 'Issued', tone: 'text-command-info border-command-info/40' },
  { status: 'ACTIVE', label: 'Active', tone: 'text-command-accent border-command-accent/40' },
  { status: 'COMPLETE', label: 'Complete', tone: 'text-command-success border-command-success/40' },
];

function safeTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : format(d, 'HH:mm · dd MMM');
}

function parseProgress(json: string | null): Progress {
  if (json) {
    try {
      const p = JSON.parse(json);
      if (p && typeof p.advancedIndex === 'number' && Array.isArray(p.advances)) return p;
    } catch { /* fall through */ }
  }
  return { advancedIndex: -1, advances: [] };
}

// ─── Plan editor (create / edit draft) ──────────────────────────────────────

function PlanEditor({ plan, onClose }: { plan: Plan | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const units = useUnitStore((s) => s.units);
  const { user } = useAuthStore();
  const isEdit = !!plan;

  const [name, setName] = useState(plan?.name ?? '');
  const [unitId, setUnitId] = useState(plan?.unitId ?? user?.unitId ?? '');
  const [hHour, setHHour] = useState(plan?.hHour ? plan.hHour.slice(0, 16) : '');
  const [blocks, setBlocks] = useState<PlanBlock[]>(parsePlanBlocks(plan?.blocksJson));

  const selectableUnits = units.filter((u) => u.role !== 'HQ');

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        blocks,
        hHour: hHour ? new Date(hHour).toISOString() : undefined,
      };
      return isEdit
        ? planApi.update(plan!.id, payload)
        : planApi.create({ ...payload, unitId });
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Plan updated' : 'Plan created');
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to save plan'),
  });

  const canSave = name.trim() && (isEdit || unitId) && blocks.length > 0;

  return (
    <div className="fixed inset-0 z-[900] bg-black/70 flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-2xl my-8 bg-command-panel border border-command-border rounded-xl">
        <div className="flex items-center justify-between p-4 border-b border-command-border">
          <h2 className="text-base font-semibold text-gray-100">{isEdit ? 'Edit Plan' : 'New Operation Plan'}</h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-command-border/60" aria-label="Close">
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] font-tactical text-gray-400 mb-1">PLAN NAME</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Op NIGHTHAWK"
                className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm" />
            </label>
            <label className="block">
              <span className="block text-[11px] font-tactical text-gray-400 mb-1">UNIT</span>
              <select value={unitId} disabled={isEdit} onChange={(e) => setUnitId(e.target.value)}
                className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm disabled:opacity-60">
                <option value="">Select unit…</option>
                {selectableUnits.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.code})</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="block text-[11px] font-tactical text-gray-400 mb-1">H-HOUR (optional)</span>
            <input type="datetime-local" value={hHour} onChange={(e) => setHHour(e.target.value)}
              className="px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm" />
          </label>

          <div>
            <span className="block text-[11px] font-tactical text-gray-400 mb-2">BLOCK SEQUENCE</span>
            <BlockSequenceEditor blocks={blocks} onChange={setBlocks} />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-command-border">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-gray-100 border border-command-border">
            Cancel
          </button>
          <button type="button" onClick={() => saveMutation.mutate()} disabled={!canSave || saveMutation.isPending}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-command-accent text-command-bg hover:bg-command-accent/90 disabled:opacity-50">
            {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save Plan' : 'Create Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Plan detail (checklist + live progress + plan-vs-actual + issue) ───────

function PlanDetail({ planId, onClose, onEdit }: { planId: string; onClose: () => void; onEdit: (p: Plan) => void }) {
  const queryClient = useQueryClient();
  const platoons = usePlatoonStore((s) => s.platoons);
  const [issuePlatoon, setIssuePlatoon] = useState('');

  const planQ = useQuery({
    queryKey: ['plan', planId],
    queryFn: async () => (await planApi.getById(planId)).data?.data?.plan as Plan,
    refetchInterval: 8000,
  });

  const issueMutation = useMutation({
    mutationFn: () => planApi.issue(planId, { platoonId: issuePlatoon }),
    onSuccess: () => {
      toast.success('Plan issued — auto-FragO sent to platoon');
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['plan', planId] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to issue plan'),
  });

  const plan = planQ.data;
  const blocks = useMemo(() => parsePlanBlocks(plan?.blocksJson), [plan?.blocksJson]);
  const progress = useMemo(() => parseProgress(plan?.progressJson ?? null), [plan?.progressJson]);
  const totalWeight = useMemo(() => blocks.reduce((s, b) => s + (b.weight || 1), 0), [blocks]);
  const advanceAt = useMemo(() => {
    const m = new Map<number, string>();
    progress.advances.forEach((a) => m.set(a.index, a.at));
    return m;
  }, [progress]);

  // Plan-vs-actual pace: projected cumulative weight fraction vs actual elapsed
  // fraction since H-Hour, at the latest advanced block.
  const pace = useMemo(() => {
    if (!plan?.hHour || progress.advancedIndex < 0 || totalWeight === 0) return null;
    const start = new Date(plan.hHour).getTime();
    const last = advanceAt.get(progress.advancedIndex);
    if (!last) return null;
    const cumWeight = blocks.slice(0, progress.advancedIndex + 1).reduce((s, b) => s + (b.weight || 1), 0);
    const projectedFrac = cumWeight / totalWeight;
    const elapsedMin = Math.max(0, (new Date(last).getTime() - start) / 60000);
    return { projectedFrac, elapsedMin };
  }, [plan?.hHour, progress.advancedIndex, totalWeight, advanceAt, blocks]);

  return (
    <div className="fixed inset-0 z-[900] bg-black/70 flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-2xl my-8 bg-command-panel border border-command-border rounded-xl">
        <div className="flex items-center justify-between p-4 border-b border-command-border">
          <div>
            <h2 className="text-base font-semibold text-gray-100">{plan?.name ?? 'Plan'}</h2>
            <p className="text-[11px] text-gray-500">
              {plan?.unit?.code} · {plan?.status}
              {plan?.callsign ? ` · ${plan.callsign}` : ''}
              {plan?.hHour ? ` · H ${safeTime(plan.hHour)}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-command-border/60" aria-label="Close">
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {pace && (
            <div className="rounded-lg border border-command-border bg-command-bg p-3 text-xs text-gray-300">
              <span className="font-tactical text-gray-500">PACE · </span>
              {Math.round(pace.projectedFrac * 100)}% of the sequence complete after{' '}
              {Math.round(pace.elapsedMin)} min since H-Hour.
            </div>
          )}

          {/* Block checklist with live progress */}
          <ol className="space-y-1.5">
            {blocks.map((b, i) => {
              const def = blockDef(b.blockId);
              const done = i <= progress.advancedIndex;
              const current = i === progress.advancedIndex && plan?.status === 'ACTIVE';
              const phase = b.phase ?? def?.phase;
              return (
                <li key={i} className={clsx('rounded-lg border p-2.5 flex items-center gap-2',
                  done ? 'border-command-success/40 bg-command-success/5' : 'border-command-border bg-command-bg',
                  current && 'ring-1 ring-command-accent')}>
                  <span className={clsx('w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0',
                    done ? 'bg-command-success text-command-bg' : 'bg-command-border text-gray-400')}>
                    {done ? '✓' : i + 1}
                  </span>
                  <span aria-hidden>{def?.icon ?? '📄'}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-100">{def?.label ?? b.blockId}</span>
                      {phase && (
                        <span className="text-[9px] font-bold font-tactical" style={{ color: PHASE_META[phase].color }}>
                          {PHASE_META[phase].label}
                        </span>
                      )}
                      <span className="ml-auto text-[10px] text-gray-500 font-tactical">w{b.weight}</span>
                    </div>
                    {typeof b.params.note === 'string' && b.params.note && (
                      <div className="text-[11px] text-gray-500 truncate">{b.params.note}</div>
                    )}
                  </div>
                  {advanceAt.get(i) && (
                    <span className="text-[10px] text-command-success font-tactical flex-shrink-0">{safeTime(advanceAt.get(i))}</span>
                  )}
                </li>
              );
            })}
          </ol>

          {/* Issue action (DRAFT/ISSUED only) */}
          {plan && (plan.status === 'DRAFT' || plan.status === 'ISSUED') && (
            <div className="rounded-lg border border-command-border bg-command-bg p-3 space-y-2">
              <span className="block text-[11px] font-tactical text-gray-400">ISSUE TO PLATOON</span>
              <div className="flex flex-wrap items-center gap-2">
                <select value={issuePlatoon} onChange={(e) => setIssuePlatoon(e.target.value)}
                  className="px-2 py-1.5 rounded bg-command-panel border border-command-border text-xs text-gray-100">
                  <option value="">Select platoon…</option>
                  {platoons.filter((p) => !plan.unitId || p.unitId === plan.unitId).map((p) =>
                    <option key={p.id} value={p.id}>{p.callsign}</option>)}
                </select>
                <button type="button" disabled={!issuePlatoon || issueMutation.isPending}
                  onClick={() => {
                    if (!window.confirm('Issue this plan? An auto-FragO will be sent to the platoon.')) return;
                    issueMutation.mutate();
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-command-accent text-command-bg hover:bg-command-accent/90 disabled:opacity-50">
                  {issueMutation.isPending ? 'Issuing…' : plan.status === 'ISSUED' ? 'Re-issue' : 'Issue Plan'}
                </button>
                {plan.status === 'DRAFT' && (
                  <button type="button" onClick={() => onEdit(plan)}
                    className="px-3 py-1.5 rounded-lg text-xs text-gray-300 border border-command-border hover:text-white">
                    Edit
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function OpPlansPage() {
  const fetchPlatoons = usePlatoonStore((s) => s.fetchPlatoons);
  const platoons = usePlatoonStore((s) => s.platoons);
  const units = useUnitStore((s) => s.units);
  const fetchUnits = useUnitStore((s) => s.fetchUnits);
  const [editing, setEditing] = useState<{ open: boolean; plan: Plan | null }>({ open: false, plan: null });
  const [detailId, setDetailId] = useState<string | null>(null);

  useEffect(() => {
    if (platoons.length === 0) fetchPlatoons();
    if (units.length === 0) fetchUnits();
  }, [platoons.length, units.length, fetchPlatoons, fetchUnits]);

  const plansQ = useQuery({
    queryKey: ['plans'],
    queryFn: async () => (await planApi.getAll({ limit: 200 })).data?.data?.plans as Plan[],
    retry: false,
    refetchInterval: 15000,
  });

  const byLane = useMemo(() => {
    const map: Record<PlanStatus, Plan[]> = { DRAFT: [], ISSUED: [], ACTIVE: [], COMPLETE: [] };
    (plansQ.data ?? []).forEach((p) => { if (map[p.status]) map[p.status].push(p); });
    return map;
  }, [plansQ.data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-display font-bold text-gray-100">Operation Plans</h1>
          <p className="text-gray-500 text-[11px]">Author ontology block sequences · issue to platoons · track live progress</p>
        </div>
        <button type="button" onClick={() => setEditing({ open: true, plan: null })}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-command-accent text-command-bg text-sm font-semibold hover:bg-command-accent/90">
          <PlusIcon className="w-4 h-4" /> New Plan
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {LANES.map((lane) => (
          <div key={lane.status} className="bg-command-panel border border-command-border rounded-xl flex flex-col">
            <div className={clsx('p-3 border-b border-command-border text-sm font-semibold', lane.tone)}>
              {lane.label} <span className="text-gray-500">({byLane[lane.status].length})</span>
            </div>
            <div className="p-2 space-y-2 min-h-[120px]">
              {byLane[lane.status].map((p) => {
                const blocks = parsePlanBlocks(p.blocksJson);
                const progress = parseProgress(p.progressJson);
                const doneCount = progress.advancedIndex + 1;
                return (
                  <button key={p.id} type="button" onClick={() => setDetailId(p.id)}
                    className="w-full text-left rounded-lg border border-command-border bg-command-bg p-2.5 hover:border-command-accent/50 transition-colors">
                    <div className="text-sm font-semibold text-gray-100 truncate">{p.name}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {p.unit?.code}{p.callsign ? ` · ${p.callsign}` : ''} · {blocks.length} blocks
                    </div>
                    {(p.status === 'ACTIVE' || p.status === 'COMPLETE') && blocks.length > 0 && (
                      <div className="mt-1.5 h-1.5 rounded-full bg-command-border overflow-hidden">
                        <div className="h-full bg-command-accent"
                          style={{ width: `${Math.round((Math.max(0, doneCount) / blocks.length) * 100)}%` }} />
                      </div>
                    )}
                  </button>
                );
              })}
              {byLane[lane.status].length === 0 && (
                <div className="py-6 text-center text-gray-600 text-xs">
                  {plansQ.isError ? '—' : 'Empty'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing.open && (
        <PlanEditor plan={editing.plan} onClose={() => setEditing({ open: false, plan: null })} />
      )}
      {detailId && (
        <PlanDetail planId={detailId} onClose={() => setDetailId(null)}
          onEdit={(p) => { setDetailId(null); setEditing({ open: true, plan: p }); }} />
      )}
    </div>
  );
}
